"use client"

import { ChevronLeft, ChevronRight } from "@untitledui/icons"
import type { Variants } from "motion/react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import type { KeyboardEvent } from "react"

import { ButtonUtility } from "@/components/ui/button-utility"
import type { TrackPrompt } from "@/types"

import { ConversationCard } from "./conversation-card"

interface ConversationCardDeckProps {
  prompts: TrackPrompt[]
  index: number
  /** +1 dealt forward, -1 dealt back — drives the deal animation. */
  direction: number
  onNavigate: (direction: 1 | -1) => void
  affirm?: boolean
  /** Extra sentence prepended to the live-region announcement (e.g. submit success). */
  announcementPrefix?: string
}

// A deck, not a scroll track. AnimatePresence deals one card at a time —
// deliberately not the Embla carousel wrapper: an overflow-hidden track
// would clip the deck-edge pseudo-cards and the deal rotation, its
// arrow-key handling fights the form sitting beside the deck, and fresh
// props after each submit would reset its internal index.
const dealVariants: Variants = {
  enter: (dir: number) => ({ x: dir * 56, rotate: dir * 4, opacity: 0 }),
  center: { x: 0, rotate: 0, opacity: 1, zIndex: 1 },
  exit: (dir: number) => ({
    x: dir * -40,
    rotate: dir * -3,
    opacity: 0,
    zIndex: 0,
  }),
}

const fadeVariants: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
}

export function ConversationCardDeck({
  prompts,
  index,
  direction,
  onNavigate,
  affirm = false,
  announcementPrefix,
}: ConversationCardDeckProps) {
  const reduceMotion = useReducedMotion()
  const prompt = prompts[index]
  if (!prompt) return null

  const variants = reduceMotion ? fadeVariants : dealVariants

  const handleArrows = (event: KeyboardEvent) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault()
      onNavigate(-1)
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      onNavigate(1)
    }
  }

  const announcement = [
    announcementPrefix,
    `Card ${index + 1} of ${prompts.length}: ${prompt.title}.`,
    `${prompt.content_type} prompt.`,
    prompt.suggestions_count > 0
      ? `${prompt.suggestions_count} ${prompt.suggestions_count === 1 ? "answer" : "answers"} so far.`
      : "No answers yet.",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label="Conversation cards"
      onKeyDown={handleArrows}
    >
      <div className="relative mx-auto w-64 sm:w-72 xl:w-80">
        {/* The rest of the deck: static edges peeking out behind the top
            card. They ARE the "there's more" affordance — never animated;
            physically the deck stays put while the top card deals. */}
        {prompts.length > 1 && (
          <>
            <div
              aria-hidden
              className="bg-batyr-cream ring-batyr-ink/10 absolute inset-0 translate-x-1.5 translate-y-1 rotate-2 rounded-2xl shadow-xs ring-1"
            />
            <div
              aria-hidden
              className="bg-batyr-cream ring-batyr-ink/10 absolute inset-0 -translate-x-1 translate-y-0.5 -rotate-1 rounded-2xl shadow-xs ring-1"
            />
          </>
        )}
        <AnimatePresence initial={false} mode="popLayout" custom={direction}>
          <motion.div
            key={prompt.id}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              duration: 0.22,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="relative"
          >
            <ConversationCard
              prompt={prompt}
              order={index + 1}
              affirm={affirm}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {prompts.length > 1 && (
        <div className="mt-5 flex items-center justify-center gap-3">
          <div className="p-1">
            <ButtonUtility
              size="sm"
              color="secondary"
              icon={ChevronLeft}
              tooltip="Previous card"
              onClick={() => onNavigate(-1)}
            />
          </div>
          <span aria-hidden className="text-tertiary text-sm tabular-nums">
            {index + 1} of {prompts.length}
          </span>
          <div className="p-1">
            <ButtonUtility
              size="sm"
              color="secondary"
              icon={ChevronRight}
              tooltip="Next card"
              onClick={() => onNavigate(1)}
            />
          </div>
        </div>
      )}

      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  )
}
